import { create } from 'zustand';
import { persist } from 'zustand/middleware';
// ── Store ─────────────────────────────────────────────────────────────────────
export const usePosStore = create()(persist((set, get) => ({
    cart: [],
    customer: null,
    payments: [],
    appliedVoucher: null,
    orderNote: '',
    activeSession: null,
    locationId: null,
    registerId: null,
    heldOrders: [],
    offlineQueue: [],
    isOffline: false,
    lastOrderNumber: null,
    offlineOrderCounter: 0,
    // ── Cart ─────────────────────────────────────────────────────────────
    addItem: (item) => set((s) => {
        const key = (i) => `${i.product_id}-${i.variant_id ?? ''}`;
        const k = `${item.product_id}-${item.variant_id ?? ''}`;
        const full = { discount_cents: 0, tax_rate: 10, loyalty_points_earn: 0, ...item };
        const existing = s.cart.find(c => key(c) === k);
        if (existing) {
            return { cart: s.cart.map(c => key(c) === k ? { ...c, quantity: c.quantity + (item.quantity ?? 1) } : c) };
        }
        return { cart: [...s.cart, full] };
    }),
    updateQty: (product_id, qty, variant_id) => set((s) => {
        const key = `${product_id}-${variant_id ?? ''}`;
        return {
            cart: qty <= 0
                ? s.cart.filter(c => `${c.product_id}-${c.variant_id ?? ''}` !== key)
                : s.cart.map(c => `${c.product_id}-${c.variant_id ?? ''}` === key ? { ...c, quantity: qty } : c),
        };
    }),
    removeItem: (product_id, variant_id) => set((s) => ({
        cart: s.cart.filter(c => !(c.product_id === product_id && c.variant_id === variant_id)),
    })),
    setItemNote: (product_id, note) => set((s) => ({
        cart: s.cart.map(c => c.product_id === product_id ? { ...c, note } : c),
    })),
    setItemDiscount: (product_id, discount_cents) => set((s) => ({
        cart: s.cart.map(c => c.product_id === product_id
            ? { ...c, discount_cents: Math.max(0, Math.min(discount_cents, c.price_cents * c.quantity)) }
            : c),
    })),
    setCustomer: (customer) => set({ customer }),
    addPayment: (payment) => set(s => ({ payments: [...s.payments.filter(p => p.method !== payment.method), payment] })),
    updatePayment: (method, amount_cents) => set(s => ({ payments: s.payments.map(p => p.method === method ? { ...p, amount_cents } : p) })),
    removePayment: (method) => set(s => ({ payments: s.payments.filter(p => p.method !== method) })),
    applyVoucher: (voucher) => set({ appliedVoucher: voucher }),
    clearVoucher: () => set({ appliedVoucher: null }),
    setOrderNote: (note) => set({ orderNote: note }),
    // ── Hold / resume ─────────────────────────────────────────────────────
    holdOrder: (name) => set(s => {
        if (s.cart.length === 0)
            return s;
        const held = {
            id: crypto.randomUUID(), name,
            cart: s.cart, customer: s.customer,
            payments: s.payments, voucher: s.appliedVoucher,
            savedAt: new Date().toISOString(),
        };
        return {
            heldOrders: [...s.heldOrders, held],
            cart: [], customer: null, payments: [], appliedVoucher: null, orderNote: '',
        };
    }),
    resumeOrder: (id) => set(s => {
        const held = s.heldOrders.find(h => h.id === id);
        if (!held)
            return s;
        return {
            cart: held.cart, customer: held.customer,
            payments: held.payments, appliedVoucher: held.voucher,
            heldOrders: s.heldOrders.filter(h => h.id !== id),
        };
    }),
    deleteHeld: (id) => set(s => ({ heldOrders: s.heldOrders.filter(h => h.id !== id) })),
    // ── Offline queue ─────────────────────────────────────────────────────
    queueOfflineOrder: () => {
        const s = get();
        const counter = s.offlineOrderCounter + 1;
        const order = {
            id: crypto.randomUUID(),
            order_number: `OFF-${String(counter).padStart(4, '0')}`,
            cart: s.cart,
            customer: s.customer,
            payments: s.payments,
            voucher: s.appliedVoucher,
            location_id: s.locationId,
            register_id: s.registerId,
            order_note: s.orderNote,
            queued_at: new Date().toISOString(),
            sync_status: 'pending',
        };
        set(st => ({
            offlineQueue: [...st.offlineQueue, order],
            offlineOrderCounter: counter,
            cart: [], customer: null, payments: [], appliedVoucher: null, orderNote: '',
        }));
        return order;
    },
    updateOfflineStatus: (id, status, error, serverId) => set(s => ({
        offlineQueue: s.offlineQueue.map(o => o.id === id
            ? { ...o, sync_status: status, sync_error: error, server_order_id: serverId ?? o.server_order_id }
            : o),
    })),
    removeOfflineOrder: (id) => set(s => ({
        offlineQueue: s.offlineQueue.filter(o => o.id !== id),
    })),
    clearSyncedOrders: () => set(s => ({
        offlineQueue: s.offlineQueue.filter(o => o.sync_status !== 'synced'),
    })),
    setOffline: (offline) => set({ isOffline: offline }),
    // ── Session ───────────────────────────────────────────────────────────
    clearCart: () => set({ cart: [], customer: null, payments: [], appliedVoucher: null, orderNote: '' }),
    setSession: (session) => set({ activeSession: session }),
    setLocation: (locationId, registerId) => set({ locationId, registerId }),
    setLastOrder: (orderNumber) => set({ lastOrderNumber: orderNumber }),
    // ── Computed ──────────────────────────────────────────────────────────
    subtotalCents: () => {
        const { cart } = get();
        return cart.reduce((sum, i) => sum + (i.price_cents * i.quantity) - i.discount_cents, 0);
    },
    taxCents: () => {
        const { cart, appliedVoucher } = get();
        const subtotal = cart.reduce((sum, i) => sum + (i.price_cents * i.quantity) - i.discount_cents, 0);
        const afterDiscount = Math.max(0, subtotal - (appliedVoucher?.discount_cents ?? 0));
        const totalWeight = cart.reduce((sum, i) => sum + (i.price_cents * i.quantity), 0);
        if (totalWeight === 0)
            return 0;
        const weightedRate = cart.reduce((sum, i) => sum + (i.tax_rate ?? 10) * (i.price_cents * i.quantity), 0) / totalWeight;
        return Math.round(afterDiscount * weightedRate / (100 + weightedRate));
    },
    discountCents: () => get().appliedVoucher?.discount_cents ?? 0,
    totalCents: () => Math.max(0, get().subtotalCents() - get().discountCents()),
    paidCents: () => get().payments.reduce((s, p) => s + p.amount_cents, 0),
    remainingCents: () => Math.max(0, get().totalCents() - get().paidCents()),
    changeCents: () => Math.max(0, get().paidCents() - get().totalCents()),
    loyaltyPoints: () => {
        const { cart, customer } = get();
        if (!customer)
            return 0;
        return cart.reduce((sum, i) => sum + (i.loyalty_points_earn ?? 0) * i.quantity, 0);
    },
    pendingOfflineCount: () => get().offlineQueue.filter(o => o.sync_status === 'pending').length,
}), {
    name: 'earnpos-pos',
    partialize: (s) => ({
        heldOrders: s.heldOrders,
        offlineQueue: s.offlineQueue,
        offlineOrderCounter: s.offlineOrderCounter,
        locationId: s.locationId,
        registerId: s.registerId,
        activeSession: s.activeSession,
        lastOrderNumber: s.lastOrderNumber,
    }),
}));
